import React, { useState } from "react";
import { useLogger } from "@noom/hive-analytics";
import {
  Text,
  Heading,
  Button,
  ThemeProvider,
  neoTheme,
} from "@noom/wax-component-library";

import { WEB_BUYFLOW_DOMAIN } from "@modules/common/constants";
import type { NoomIntakeCompletionType } from "@modules/intake/updateNoomIntakeStatus";

import { useUpdateNoomIntakeStatus } from "../hooks/useUpdateNoomIntakeStatus";

export type NoomAcknowledgementIntakePageProps = {
  /** Shown as the main heading at the top of the acknowledgement. */
  title: string;
  /** Posted to healthcare API when the user confirms. */
  intakeType: NoomIntakeCompletionType;
  /** Mixpanel event name (e.g. a `MixpanelTypes` value) logged after a successful complete. */
  mixpanelEventName: string;
  /** `data-testid` on the inline error message when completion fails. */
  errorTestId: string;
  confirmButtonLabel: string;
  /** Body copy between the title and the error / confirm button (typically `Text` blocks). */
  children: React.ReactNode;
};

/**
 * Shared layout and behavior for Noom-managed acknowledgement intakes (pharmacy switch,
 * concentration change, etc.): theme shell, complete POST, analytics, and buyflow postMessage.
 */
export const NoomAcknowledgementIntakePage: React.FC<NoomAcknowledgementIntakePageProps> = ({
  title,
  intakeType,
  mixpanelEventName,
  errorTestId,
  confirmButtonLabel,
  children,
}) => {
  const mixpanelLogger = useLogger();
  const { completeIntake, loading, error } = useUpdateNoomIntakeStatus();
  const [submitted, setSubmitted] = useState(false);

  const handleConfirm = async () => {
    const ok = await completeIntake(intakeType);
    if (ok) {
      mixpanelLogger.log(mixpanelEventName);
      setSubmitted(true);
      window.parent.postMessage({ event: "INTAKE_COMPLETE" }, WEB_BUYFLOW_DOMAIN!);
    }
  };

  return (
    <ThemeProvider theme={neoTheme}>
      <div style={{ backgroundColor: "white", minHeight: "100vh" }}>
        <div style={{ padding: "24px", maxWidth: "600px", margin: "0 auto" }}>
          <Heading size="sm" fontWeight="700">
            {title}
          </Heading>
          {children}

          {error && (
            <Text
              size="md"
              fontWeight="400"
              marginTop="16px"
              display="block"
              color="red"
              data-testid={errorTestId}
            >
              {error}
            </Text>
          )}
          <Button
            colorScheme="primary"
            isFullWidth={true}
            marginTop="24px"
            isLoading={loading}
            isDisabled={submitted}
            onClick={handleConfirm}
          >
            {confirmButtonLabel}
          </Button>
        </div>
      </div>
    </ThemeProvider>
  );
};
